// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

axios.defaults.withCredentials = true;

axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-CSRF-Token'] = '';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState('');
  
  // Configurar axios para incluir CSRF token en todas las solicitudes
  useEffect(() => {
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
    }
  }, [csrfToken]);
  
  // Verificar si el usuario ya está autenticado al cargar la aplicación - MODIFICADO
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get('/api/auth/me');
        setCurrentUser(response.data);
        setCsrfToken(response.data.csrfToken);
        
        // Guardar imagen de perfil en localStorage si existe
        if (response.data.profileImage) {
          localStorage.setItem('user-profile-image', response.data.profileImage);
        } else {
          // Si no hay imagen en la base de datos, limpiar localStorage
          localStorage.removeItem('user-profile-image');
        }
      } catch (error) {
        setCurrentUser(null);
        // Limpiar datos relacionados si hay error de autenticación
        localStorage.removeItem('user-profile-image');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);
  
  // Función para iniciar sesión - MODIFICADO
  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      setCurrentUser(response.data);
      setCsrfToken(response.data.csrfToken);
      
      // Guardar imagen de perfil en localStorage si viene en la respuesta del login
      if (response.data.profileImage) {
        localStorage.setItem('user-profile-image', response.data.profileImage);
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.error || 'Error al iniciar sesión' 
      };
    }
  };
  
  // Función para cerrar sesión - MODIFICADO
  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
      setCurrentUser(null);
      setCsrfToken('');
      
      // Limpiar la imagen de perfil al cerrar sesión
      localStorage.removeItem('user-profile-image');
      
      return { success: true };
    } catch (error) {
      // Incluso si hay error en el servidor, limpiar el estado local
      setCurrentUser(null);
      setCsrfToken('');
      localStorage.removeItem('user-profile-image');
      
      return { 
        success: false, 
        message: error.response?.data?.error || 'Error al cerrar sesión' 
      };
    }
  };
  
  // Función para cambiar contraseña
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post('/api/auth/change-password', { 
        currentPassword, 
        newPassword,
        _csrf: csrfToken
      });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.error || 'Error al cambiar la contraseña' 
      };
    }
  };
  
  // Función para refrescar el token CSRF
  const refreshCsrfToken = async () => {
    try {
      const response = await axios.get('/api/csrf-token', { withCredentials: true });
      const newToken = response.data.csrfToken;
      setCsrfToken(newToken);
      axios.defaults.headers.common['X-CSRF-Token'] = newToken;
      console.log('CSRF token refreshed:', newToken); // Agregar log para depuración
      return { success: true, token: newToken };
    } catch (error) {
      console.error('Error al obtener token CSRF:', error);
      return { success: false, message: error.response?.data?.error || 'Error al obtener token CSRF' };
    }
  };
  
  // NUEVA FUNCIÓN - Para actualizar los datos del usuario en el contexto
  const updateUserData = (userData) => {
    setCurrentUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
    
    // Si se actualiza la imagen de perfil, también actualizar localStorage
    if (userData.profileImage) {
      localStorage.setItem('user-profile-image', userData.profileImage);
    }
  };

  // NUEVA FUNCIÓN - Para refrescar los datos del usuario actual desde el servidor
  const refreshUserData = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setCurrentUser(response.data);
      setCsrfToken(response.data.csrfToken);
      
      // Guardar imagen de perfil en localStorage si existe
      if (response.data.profileImage) {
        localStorage.setItem('user-profile-image', response.data.profileImage);
      } else {
        localStorage.removeItem('user-profile-image');
      }
      
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Error al refrescar datos del usuario:', error);
      return { success: false, error: error.response?.data?.error || 'Error al refrescar datos' };
    }
  };
  
  const value = {
    currentUser,
    loading,
    login,
    logout,
    changePassword,
    refreshCsrfToken,
    updateUserData, // Nueva función exportada
    refreshUserData, // Nueva función para refrescar datos del usuario
    csrfToken
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
